import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    CalendarDays,
    ArrowRight,
    X,
    Search,
    Layers,
    ArrowUpDown,
    Briefcase,
    ExternalLink,
} from "lucide-react";
import { recentvacancyApi } from "../../../api/api";
import api from "../../../utils/api";
import Navbar from "../../../components/navbar/Navbar";
import Footer from "../../../components/footer/Footer";

const RecentVacancies = () => {
    const { type } = useParams(); // 'upsc', 'state-psc', 'other', or undefined
    const [vacancies, setVacancies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortOrder, setSortOrder] = useState("newest");

    // Normalize URL param for the initial category state
    const getInitialCategory = (param) => {
        if (!param) return "all";
        const lower = param.toLowerCase();
        if (lower === "upsc") return "upsc";
        if (lower === "state-psc" || lower === "state psc") return "state-psc";
        if (lower === "other") return "other";
        return "all";
    };

    const [categoryFilter, setCategoryFilter] = useState(
        getInitialCategory(type),
    );

    // Sync state if URL changes while component is mounted
    useEffect(() => {
        setCategoryFilter(getInitialCategory(type));
    }, [type]);

    // Fetch ALL vacancies once
    useEffect(() => {
        const fetchVacancies = async () => {
            setLoading(true);
            try {
                const res = await api.get(`${recentvacancyApi}`);
                const data = res.data;

                let vacanciesData = [];
                if (Array.isArray(data)) {
                    vacanciesData = data;
                } else if (data?.status === true && Array.isArray(data?.data)) {
                    vacanciesData = data.data;
                }

                if (vacanciesData.length > 0) {
                    setVacancies(vacanciesData);
                } else {
                    toast.warn("No recent vacancies available.");
                }
            } catch (error) {
                console.error("Error fetching vacancies:", error);
                toast.error("Failed to load vacancies. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        fetchVacancies();
    }, []);

    const [selectedVacancy, setSelectedVacancy] = useState(null);

    // Apply Search, Category Filter, and Sorting
    const filteredVacancies = vacancies
        .filter((vacancy) => {
            // 1. Search Query
            const query = searchQuery.toLowerCase();
            const title = vacancy?.title?.toLowerCase() || "";
            const description = vacancy?.description?.toLowerCase() || "";
            const matchesSearch =
                title.includes(query) || description.includes(query);

            // 2. Category Filter
            // (Checking both vacancy_type and select_vacancy to handle varied API data structures)
            const vType1 = vacancy?.vacancy_type?.toLowerCase() || "";
            const vType2 = vacancy?.select_vacancy?.toLowerCase() || "";

            const isUpsc = vType1 === "upsc" || vType2 === "upsc";
            const isStatePsc =
                vType1 === "state-psc" ||
                vType1 === "state psc" ||
                vType2 === "state-psc" ||
                vType2 === "state psc";

            let matchesCategory = true;
            if (categoryFilter === "upsc") matchesCategory = isUpsc;
            else if (categoryFilter === "state-psc")
                matchesCategory = isStatePsc;
            else if (categoryFilter === "other")
                matchesCategory = !isUpsc && !isStatePsc;
            // if "all", matchesCategory remains true

            return matchesSearch && matchesCategory;
        })
        .sort((a, b) => {
            // 3. Sorting
            const dateA = new Date(a.created_at || 0);
            const dateB = new Date(b.created_at || 0);
            return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
        });

    const openPopup = (vacancy) => {
        setSelectedVacancy(vacancy);
        document.body.style.overflow = "hidden";
    };

    const closePopup = () => {
        setSelectedVacancy(null);
        document.body.style.overflow = "auto";
    };

    const truncateHTML = (html, wordLimit) => {
        if (!html) return "";
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;

        let wordCount = 0;
        const truncateNode = (node) => {
            if (wordCount >= wordLimit) return null;
            if (node.nodeType === Node.TEXT_NODE) {
                const words = node.textContent.split(/\s+/);
                const remaining = wordLimit - wordCount;
                if (words.length > remaining) {
                    wordCount += remaining;
                    return document.createTextNode(
                        words.slice(0, remaining).join(" ") + "...",
                    );
                } else {
                    wordCount += words.length;
                    return node.cloneNode();
                }
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                const clone = node.cloneNode(false);
                node.childNodes.forEach((child) => {
                    if (wordCount < wordLimit) {
                        const truncatedChild = truncateNode(child);
                        if (truncatedChild) clone.appendChild(truncatedChild);
                    }
                });
                return clone;
            }
            return null;
        };

        const fragment = document.createDocumentFragment();
        tempDiv.childNodes.forEach((child) => {
            if (wordCount < wordLimit) {
                const truncatedChild = truncateNode(child);
                if (truncatedChild) fragment.appendChild(truncatedChild);
            }
        });

        const container = document.createElement("div");
        container.appendChild(fragment);
        return container.innerHTML;
    };

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 15 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.4, ease: "easeOut" },
        },
    };

    return (
        <div className="bg-gray-50 min-h-screen flex flex-col">
            

            <main className="flex-grow pt-32 pb-24 px-4 sm:px-6 lg:px-8 w-full">
                <div className="max-w-7xl mx-auto">
                    {/* Header & Controls Section */}
                    <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12 border-b border-gray-200 pb-8">
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="max-w-2xl"
                        >
                            <div className="flex items-center gap-2 text-red-700 font-bold text-xs uppercase mb-3">
                                <span className="w-6 h-1 bg-red-700"></span>
                                Career Opportunities
                            </div>
                            <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4">
                                Recent{" "}
                                <span className="text-yellow-500">
                                    Vacancies.
                                </span>
                            </h2>
                            <p className="text-gray-500 text-lg font-medium leading-relaxed">
                                Stay updated with the latest recruitment
                                notifications, eligibility criteria, and
                                application deadlines.
                            </p>
                        </motion.div>

                        {/* Separated Modern Controls */}
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            className="flex flex-col sm:flex-row flex-wrap xl:flex-nowrap gap-4 w-full lg:w-auto"
                        >
                            {/* Search */}
                            <div className="relative flex-grow sm:w-56">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Search
                                        size={16}
                                        className="text-gray-400"
                                    />
                                </div>
                                <input
                                    type="text"
                                    placeholder="Search jobs..."
                                    className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-lg text-sm font-medium text-neutral-900 focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all shadow-sm"
                                    value={searchQuery}
                                    onChange={(e) =>
                                        setSearchQuery(e.target.value)
                                    }
                                />
                            </div>

                            {/* Category Filter */}
                            <div className="relative sm:w-48">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Layers
                                        size={16}
                                        className="text-gray-400"
                                    />
                                </div>
                                <select
                                    className="w-full pl-10 pr-8 py-3 bg-white border border-gray-200 rounded-lg text-sm font-bold text-neutral-900 appearance-none focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all shadow-sm cursor-pointer"
                                    value={categoryFilter}
                                    onChange={(e) =>
                                        setCategoryFilter(e.target.value)
                                    }
                                >
                                    <option value="all">All Categories</option>
                                    <option disabled>──────────</option>
                                    <option value="upsc">UPSC Only</option>
                                    <option value="state-psc">
                                        State PSC Only
                                    </option>
                                    <option value="other">Other Exams</option>
                                </select>
                                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                    <ChevronDownIcon />
                                </div>
                            </div>

                            {/* Sort Order */}
                            <div className="relative sm:w-44">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <ArrowUpDown
                                        size={16}
                                        className="text-gray-400"
                                    />
                                </div>
                                <select
                                    className="w-full pl-10 pr-8 py-3 bg-white border border-gray-200 rounded-lg text-sm font-bold text-neutral-900 appearance-none focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all shadow-sm cursor-pointer"
                                    value={sortOrder}
                                    onChange={(e) =>
                                        setSortOrder(e.target.value)
                                    }
                                >
                                    <option value="newest">Newest First</option>
                                    <option value="oldest">Oldest First</option>
                                </select>
                                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                    <ChevronDownIcon />
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    {/* Content Grid */}
                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <div
                                    key={i}
                                    className="bg-white rounded-xl border border-gray-200 shadow-sm animate-pulse h-64 p-6 flex flex-col"
                                >
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="w-12 h-12 bg-gray-100 rounded-lg"></div>
                                        <div className="w-24 h-6 bg-gray-100 rounded"></div>
                                    </div>
                                    <div className="w-3/4 h-6 bg-gray-100 rounded mb-4"></div>
                                    <div className="w-full h-4 bg-gray-100 rounded mb-2"></div>
                                    <div className="w-5/6 h-4 bg-gray-100 rounded mb-6"></div>
                                    <div className="mt-auto w-1/3 h-4 bg-gray-100 rounded"></div>
                                </div>
                            ))}
                        </div>
                    ) : filteredVacancies.length === 0 ? (
                        <div className="text-center py-20 bg-white rounded-xl border border-gray-200 shadow-sm">
                            <Briefcase
                                size={40}
                                className="mx-auto text-gray-300 mb-4"
                            />
                            <p className="text-neutral-900 font-bold text-lg mb-2">
                                No Vacancies Found
                            </p>
                            <p className="text-gray-500 font-medium text-sm">
                                Try adjusting your search criteria or filter
                                options.
                            </p>
                        </div>
                    ) : (
                        <motion.div
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                        >
                            {filteredVacancies
                                .filter(
                                    (vacancy) =>
                                        vacancy.status === true ||
                                        vacancy.status === 1,
                                )
                                .map((vacancy, index) => {
                                    const shouldTruncate =
                                        (vacancy.description?.split(/\s+/)
                                            .length || 0) > 15;
                                    const displayHTML = shouldTruncate
                                        ? truncateHTML(vacancy.description, 15)
                                        : vacancy.description;

                                    return (
                                        <motion.div
                                            variants={itemVariants}
                                            key={vacancy.id || index}
                                            onClick={() => openPopup(vacancy)}
                                            className="group relative bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-neutral-900 transition-all duration-300 flex flex-col overflow-hidden cursor-pointer"
                                        >
                                            {/* Hover Line */}
                                            <div className="absolute top-0 left-0 w-full h-[3px] bg-yellow-400 scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-500 ease-out z-10"></div>

                                            <div className="p-6 flex flex-col flex-grow">
                                                {/* Card Header: Icon & Date */}
                                                <div className="flex justify-between items-start mb-5">
                                                    <div className="w-12 h-12 bg-gray-50 group-hover:bg-yellow-400 text-neutral-900 rounded-lg flex items-center justify-center transition-colors duration-300 border border-gray-100 group-hover:border-yellow-400">
                                                        <Briefcase size={20} />
                                                    </div>
                                                    <div className="flex items-center px-2.5 py-1 bg-gray-50 border border-gray-100 text-gray-500 rounded font-bold text-[10px] uppercase tracking-widest group-hover:border-gray-200 transition-colors">
                                                        <CalendarDays
                                                            size={12}
                                                            className="mr-1.5"
                                                        />
                                                        {vacancy.created_at
                                                            ? new Date(
                                                                  vacancy.created_at,
                                                              ).toLocaleDateString(
                                                                  "en-GB",
                                                                  {
                                                                      day: "2-digit",
                                                                      month: "short",
                                                                      year: "numeric",
                                                                  },
                                                              )
                                                            : "N/A"}
                                                    </div>
                                                </div>

                                                <h3 className="text-xl font-black text-neutral-900 mb-3 line-clamp-2 group-hover:text-red-700 transition-colors">
                                                    {vacancy.title}
                                                </h3>

                                                <div
                                                    className="prose prose-sm text-gray-500 leading-relaxed font-medium line-clamp-3 mb-6 flex-grow"
                                                    dangerouslySetInnerHTML={{
                                                        __html: displayHTML,
                                                    }}
                                                />

                                                <div className="mt-auto pt-4 border-t border-gray-100 flex items-center text-sm font-bold text-neutral-900 group-hover:text-yellow-600 transition-colors">
                                                    View Details
                                                    <ArrowRight
                                                        size={16}
                                                        className="ml-2 transform group-hover:translate-x-1 transition-transform"
                                                    />
                                                </div>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                        </motion.div>
                    )}
                </div>
            </main>

            

            {/* Professional Modal Popup */}
            <AnimatePresence>
                {selectedVacancy && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-neutral-900/60 backdrop-blur-sm"
                        onClick={closePopup}
                    >
                        <motion.div
                            initial={{ scale: 0.98, opacity: 0, y: 10 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.98, opacity: 0, y: 10 }}
                            transition={{ duration: 0.2 }}
                            className="bg-white rounded-xl w-full max-w-2xl shadow-2xl relative flex flex-col max-h-[90vh] border border-gray-200 overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Modal Header */}
                            <div className="bg-neutral-900 px-6 py-5 flex justify-between items-center sticky top-0 z-10">
                                <div className="flex items-center text-yellow-400 font-bold text-xs uppercase tracking-widest">
                                    <Briefcase size={14} className="mr-2" />
                                    Vacancy Details
                                </div>
                                <button
                                    className="text-gray-400 hover:text-white transition-colors p-1"
                                    onClick={closePopup}
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div className="p-6 md:p-8 overflow-y-auto custom-scrollbar flex-grow">
                                <h3 className="text-2xl md:text-3xl font-black text-neutral-900 mb-4 leading-tight">
                                    {selectedVacancy.title}
                                </h3>

                                <div className="flex items-center text-sm font-bold text-gray-500 mb-8 pb-6 border-b border-gray-100">
                                    <CalendarDays
                                        size={16}
                                        className="mr-2 text-red-700"
                                    />
                                    Posted on:{" "}
                                    {selectedVacancy.created_at
                                        ? new Date(
                                              selectedVacancy.created_at,
                                          ).toLocaleDateString("en-GB", {
                                              day: "numeric",
                                              month: "long",
                                              year: "numeric",
                                          })
                                        : "N/A"}
                                </div>

                                <div
                                    className="prose prose-neutral max-w-none text-gray-600 font-medium leading-relaxed"
                                    dangerouslySetInnerHTML={{
                                        __html: selectedVacancy.description,
                                    }}
                                />
                            </div>

                            {/* Modal Footer */}
                            {selectedVacancy.link && (
                                <div className="p-6 border-t border-gray-200 bg-gray-50">
                                    <a
                                        href={selectedVacancy.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-full flex items-center justify-center py-3.5 bg-yellow-400 hover:bg-yellow-500 text-neutral-900 font-black uppercase tracking-widest text-xs rounded-lg shadow-sm transition-colors group"
                                    >
                                        Apply Now{" "}
                                        <ExternalLink
                                            size={16}
                                            className="ml-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                                        />
                                    </a>
                                </div>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

// Helper chevron icon for select
const ChevronDownIcon = () => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-gray-400"
    >
        <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
);

export default RecentVacancies;
